import classnames from 'classnames';
import React from 'react';
import { Badge, Card } from 'react-bootstrap';
import { BsPeople } from 'react-icons/bs';
import { connect } from 'react-redux';
import {
  NavLink,
  Redirect,
  Route,
  Switch,
  useRouteMatch,
} from 'react-router-dom';
import BackgroundImages from 'v2/common/Background/BackgroundImages';
import { GenericObject, User, Permission } from 'v2/models';
import creditCardIcon from '../../icons/creditCardIcon.svg';
import gearIcon from '../../icons/gear.svg';
import layerGroupIcon from '../../icons/layer-group.svg';
import lockBlueIcon from '../../icons/lockBlue.svg';
import personIcon from '../../icons/person.svg';
import businessPeopleIcon from 'v2/icons/business-people.svg';
import DataProductsV2 from './DataProductsV2/DataProductsV2';
import MyAccount from './MyAccount/MyAccount';
import MyOrganization from './MyOrganization/MyOrganization';
import MyTeam from './MyTeam/MyTeam';
import ResetPassword from './ResetPassword/ResetPassword';
import styles from './settings.module.scss';
import Subscription from './Subscription/Subscription';
import { hasActivePermissions, hasPlanFamily } from 'v2/utils/permissions';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';
import { FEATURE_FLAGS } from 'utils/constants';
import BuyerMyTeam from './MyTeam/BuyerMyTeam/BuyerMyTeam';

const getBuyerLinks = (
  url: string,
  renderSubscription: boolean,
  isDataBuyerMyTeamPageEnabled: boolean,
  isAdmin: boolean
) => {
  const links = [
    {
      to: `${url}/account`,
      label: 'My Account',
      icon: personIcon,
    },
    {
      to: `${url}/organization`,
      label: 'My Organization',
      icon: gearIcon,
    },
  ];

  if (renderSubscription) {
    links.push({
      to: `${url}/subscription`,
      label: 'Subscription',
      icon: creditCardIcon,
    });
  }

  if (isDataBuyerMyTeamPageEnabled && isAdmin) {
    links.push({
      to: `${url}/team`,
      label: 'My Team',
      icon: businessPeopleIcon,
    });
  }

  links.push({
    to: `${url}/password`,
    label: 'Password',
    icon: lockBlueIcon,
  });

  return links;
};

const getProviderLinks = (url: string, user: User) => [
  {
    to: `${url}/account`,
    label: 'My Account',
    icon: personIcon,
  },
  {
    to: `${url}/organization`,
    label: 'My Organization',
    icon: gearIcon,
  },
  ...(user.admin
    ? [
        {
          to: `${url}/team`,
          label: 'My Team',
          icon: BsPeople,
        },
      ]
    : []),
  {
    to: `${url}/data-products`,
    label: 'Data Products',
    icon: layerGroupIcon,
  },
  {
    to: `${url}/password`,
    label: 'Password',
    icon: lockBlueIcon,
  },
];

interface SettingsProps {
  user: User;
  permissions: Record<string, Permission>;
}

const Settings = ({ user, permissions }: SettingsProps) => {
  const { path, url } = useRouteMatch();
  const isDataProvider = !!user.data_provider?.id;
  const hideIdentityAllowed = user.data_provider?.hide_identity_allowed;
  const showVagueDescription = !isDataProvider || hideIdentityAllowed;
  const { flags } = useFeatureFlagContext();
  const isDataBuyerMyTeamPageEnabled =
    flags[FEATURE_FLAGS.DISPLAY_DATA_BUYER_MY_TEAM_PAGE];

  const LINKS = !isDataProvider
    ? getBuyerLinks(
        url,
        (hasPlanFamily(permissions, 'discover') && !user.waitlisted) ||
          !hasActivePermissions(permissions),
        isDataBuyerMyTeamPageEnabled,
        user.admin
      )
    : getProviderLinks(url, user);

  return (
    <>
      <BackgroundImages />

      <div className="mt-4 container-xl fix-bg-images">
        <h1>Settings</h1>

        <Card className={classnames('h-100 px-0 mt-4', styles.cardRoot)}>
          <Card.Body className={classnames(styles.container, 'p-0 h-100')}>
            <div className="bg-light py-5 px-4 border-right border-light6">
              {LINKS.map((link, index) => {
                const Icon = link.icon;

                return (
                  <NavLink
                    key={index}
                    to={link.to}
                    className={classnames('subNavLink')}
                  >
                    <Badge pill className="mb-4 d-flex align-items-center">
                      {typeof Icon === 'string' ? (
                        <img src={Icon} className="mr-3" alt="navIcon"></img>
                      ) : (
                        <Icon className="mr-3" size={25} />
                      )}
                      {link.label}
                    </Badge>
                  </NavLink>
                );
              })}
            </div>
            <div className={classnames('p-5', styles.rightContent)}>
              <Switch>
                <Route path={`${path}/account`}>
                  <MyAccount />
                </Route>
                <Route path={`${path}/organization`}>
                  <MyOrganization
                    isDataProvider={isDataProvider}
                    isAdmin={user.admin}
                    showVagueDescription={showVagueDescription}
                  />
                </Route>
                <Route path={`${path}/team/`}>
                  {isDataProvider ? (
                    <MyTeam
                      isAdmin={user.admin}
                      isDataProvider={isDataProvider}
                      userId={user.id}
                    />
                  ) : user.admin ? (
                    <BuyerMyTeam />
                  ) : (
                    <Redirect to="/" />
                  )}
                </Route>
                <Route path={`${path}/password`}>
                  <ResetPassword />
                </Route>
                <Route path={`${path}/subscription`}>
                  <Subscription />
                </Route>
                <Route path={`${path}/data-products`}>
                  <DataProductsV2 isAdmin={user.admin} />
                </Route>
                <Route path={`${path}`}>
                  <Redirect to={`${path}/account`} />
                </Route>
              </Switch>
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

const mapStateToProps = (state: GenericObject) => {
  return {
    user: state.user.user,
    permissions: state.permissions.permissions,
  };
};

export default connect(mapStateToProps)(Settings);
